import type { PropType } from 'virtual-vue';
import type { PickerOption, PickerFieldNames } from './types';
import { DEFAULT_FIELD_NAMES } from './types';

export const DEFAULT_PICKER_MODEL_VALUE: (string | number)[] = [];
export const DEFAULT_PICKER_VISIBLE = false;
export const DEFAULT_PICKER_COLUMNS: PickerOption[] | PickerOption[][] = [];
export const DEFAULT_PICKER_TITLE = '';
export const DEFAULT_PICKER_CONFIRM_BUTTON_TEXT = '确定';
export const DEFAULT_PICKER_SHOW_CLOSE = true;
export const DEFAULT_PICKER_CLOSE_ON_CLICK_OVERLAY = true;
export const DEFAULT_PICKER_DISABLED = false;
export const DEFAULT_PICKER_TELEPORT = 'body';
export const DEFAULT_PICKER_LOCK_SCROLL = true;
export const DEFAULT_PICKER_DESTROY_ON_CLOSE = false;
export const DEFAULT_PICKER_VISIBLE_OPTION_NUM = 5;
export const DEFAULT_PICKER_OPTION_HEIGHT = 112;
export const DEFAULT_PICKER_SWIPE_DURATION = 150;

/**
 * Picker 组件的 props
 * 基于方案设计文档生成
 */
export const props = {
  /**
   * 当前选中项对应的值数组（Vue 3 v-model）
   * @default []
   */
  modelValue: {
    type: Array as PropType<(string | number)[]>,
    default: () => DEFAULT_PICKER_MODEL_VALUE,
  },
  /**
   * 当前选中项对应的值数组（Vue 2 v-model）
   * @default []
   */
  value: {
    type: Array as PropType<(string | number)[]>,
    default: () => DEFAULT_PICKER_MODEL_VALUE,
  },
  /**
   * 是否显示选择器弹窗
   * @default false
   */
  visible: {
    type: Boolean,
    default: DEFAULT_PICKER_VISIBLE,
  },
  /**
   * 选项数据配置
   * - 一维数组表示单列
   * - 二维数组表示多列（各列独立）
   * - 一维数组 + children 字段表示级联选择
   * @default []
   */
  columns: {
    type: Array as PropType<PickerOption[] | PickerOption[][]>,
    default: () => DEFAULT_PICKER_COLUMNS,
  },
  /**
   * 弹窗标题
   * @default ''
   */
  title: {
    type: String,
    default: DEFAULT_PICKER_TITLE,
  },
  /**
   * 确认按钮文字
   * @default '确定'
   */
  confirmButtonText: {
    type: String,
    default: DEFAULT_PICKER_CONFIRM_BUTTON_TEXT,
  },
  /**
   * 是否显示关闭按钮
   * @default true
   */
  showClose: {
    type: Boolean,
    default: DEFAULT_PICKER_SHOW_CLOSE,
  },
  /**
   * 点击遮罩层是否关闭
   * @default true
   */
  closeOnClickOverlay: {
    type: Boolean,
    default: DEFAULT_PICKER_CLOSE_ON_CLICK_OVERLAY,
  },
  /**
   * 是否禁用
   * @default false
   */
  disabled: {
    type: Boolean,
    default: DEFAULT_PICKER_DISABLED,
  },
  /**
   * 自定义 columns 结构中的字段名
   * @default { text: 'text', value: 'value' }
   */
  columnsFieldNames: {
    type: Object as PropType<PickerFieldNames>,
    default: () => ({ ...DEFAULT_FIELD_NAMES }),
  },
  /**
   * 指定挂载节点
   * @default 'body'
   */
  teleport: {
    type: String,
    default: DEFAULT_PICKER_TELEPORT,
  },
  /**
   * 是否锁定背景滚动
   * @default true
   */
  lockScroll: {
    type: Boolean,
    default: DEFAULT_PICKER_LOCK_SCROLL,
  },
  /**
   * 关闭时是否销毁
   * @default false
   */
  destroyOnClose: {
    type: Boolean,
    default: DEFAULT_PICKER_DESTROY_ON_CLOSE,
  },
  /**
   * 可见选项个数
   * @default 5
   */
  visibleOptionNum: {
    type: Number,
    default: DEFAULT_PICKER_VISIBLE_OPTION_NUM,
  },
  /**
   * 选项行高度（单位与 @unit 对应）
   * @default 112
   */
  optionHeight: {
    type: Number,
    default: DEFAULT_PICKER_OPTION_HEIGHT,
  },
  /**
   * 惯性滚动时长（ms）
   * @default 150
   */
  swipeDuration: {
    type: Number,
    default: DEFAULT_PICKER_SWIPE_DURATION,
  },
};
